
import React, { useState, useEffect } from 'react'
import Sidebar from '../components/Sidebar'
import DashboardTasksCard from '../components/DashboardTasksCard'
import LearnCard from '../components/LearnCard'
import ChallengesCard from '../components/ChallengesCard'
import CalendarCard from '../components/CalendarCard'
import ResourcesCard from '../components/ResourcesCard'
import UnlockCodeModal from '../components/UnlockCodeModal'
import { supabase } from '@/integrations/supabase/client'

const Dashboard = () => {
  const [showUnlockModal, setShowUnlockModal] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<string>('free')
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        
          if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('payment_status, has_entered_unlock_code, assigned_unlock_code')
            .eq('id', user.id)
            .single()
          
          const status = profile?.payment_status || 'free'
          const hasEnteredCode = profile?.has_entered_unlock_code || false
          const hasAssignedCode = !!profile?.assigned_unlock_code
          
          setPaymentStatus(status)
          
          // Show unlock modal if:
          // 1. Payment status is 'paid' (admin confirmed payment)
          // 2. User has NOT entered their unlock code yet
          // 3. User has an assigned unlock code
          if (status === 'paid' && !hasEnteredCode && hasAssignedCode) {
            setShowUnlockModal(true)
          } else if (status === 'pending') {
            // Show modal if payment is still pending (waiting for admin approval)
            setShowUnlockModal(true)
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      } finally {
        setLoading(false)
      }
    }
    
    checkPaymentStatus()
  }, [])

  const handleUnlockSuccess = () => {
    setShowUnlockModal(false)
    setPaymentStatus('paid')
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50 items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar />
      
      <main className="flex-1 p-6 overflow-hidden">
        <div className="max-w-7xl mx-auto h-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
            {/* Middle Column - Tasks, Learn & Challenges */}
            <div className="lg:col-span-9 flex flex-col gap-6 h-full overflow-hidden">
              {/* Top Half - Tasks and Learn side by side */}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 min-h-0">
                <DashboardTasksCard />
                <LearnCard />
              </div>
              
              {/* Bottom Half - Challenges */}
              <div className="flex-1 min-h-0">
                <ChallengesCard />
              </div>
            </div>

            {/* Right Column - Calendar & Resources */}
            <div className="lg:col-span-3 flex flex-col gap-6 h-full overflow-hidden">
              <div className="flex-1 min-h-0">
                <CalendarCard />
              </div>
              <div className="flex-1 min-h-0">
                <ResourcesCard />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Unlock modal ONLY for pending users */}
      {showUnlockModal && (
        <UnlockCodeModal 
          onSuccess={handleUnlockSuccess}
        />
      )}
    </div>
  )
}

export default Dashboard
